import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { SUCCESS_SENDING_RESET_PASSWORD_EMAIL } from '@/utils/messages';
import { toast_success } from '@/libs/toast-wrappers';
import { forgotPassword } from '@/api/auth';
import { ROUTES } from '@/router/routes';
import { emailSchema } from '@/utils/zod-schemas';

import { InputField } from '../form/input-field';
import { Button } from '../ui/button';
import { InputCheckbox } from '../form/input-checkbox';

const schema = z.object({
  email: emailSchema,
});

type ResetPassFormData = z.infer<typeof schema>;

export function ResetPass() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ResetPassFormData>({ resolver: zodResolver(schema) });
  const navigate = useNavigate();

  const onSubmit: SubmitHandler<ResetPassFormData> = async (data) => {
    try {
      setIsLoading(true);
      const { email } = data;

      const response = await forgotPassword(
        email,
        window.location.protocol + '//' + window.location.host + ROUTES.CHANGE_PASSWORD
      );

      if (response) {
        reset();
        setIsLoading(false);
        toast_success(SUCCESS_SENDING_RESET_PASSWORD_EMAIL);
        setTimeout(function () {
          void navigate(ROUTES.LOGIN);
        }, 2000);
      }
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <>
      <h1 className="title-auth mb-2">{t('login-reset-pass')}</h1>
      <p className="text-sm">{t('login-reset-pass-instructions')}</p>
      <form className="w-full my-12 flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <InputField
          className="login-email-input"
          label={t('login-email')}
          type="email"
          autoComplete="email"
          required
          disabled={isLoading}
          errorMessage={errors.email?.message}
          {...register('email')}
        />
        <div className="flex flex-col justify-end items-end gap-4">
          <Button href={ROUTES.LOGIN} variant="link">
            {t('login-remembered-pass')}
          </Button>
          <Button href={ROUTES.CHANGE_PASSWORD} variant="link">
            {t('login-change-pass-question')}
          </Button>
        </div>

        <InputCheckbox name="terms" required label={t('login-terms')} />
        <div className="flex flex-wrap items-center gap-4">
          <Button type="submit" className="button" loading={isLoading}>
            {t('login-submit-pass-reset')}
          </Button>
          <div className="inline-flex gap-4 items-center">
            <span>{t('or')}</span>
            <Button href={ROUTES.LOGIN} variant="link">
              {t('changepass-login')}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
